import type { ReactElement, ReactNode } from 'react';
import { Component } from 'react';

import { createPopper } from '@popperjs/core';

import { Portal } from '@skyscanner/backpack-web/bpk-react-utils';

import type { Nullable } from '@shared/types/utils';

type Props = {
  placement?: string;
  portalClassName?: string;
  target: ReactNode;
  children: ReactNode;
  renderTarget: Nullable<() => Nullable<HTMLElement>>;
};

type State = {
  isOpen: boolean;
  isChildHover: boolean;
};

const DELAY_TIME = 310;

const defaultProps = { placement: 'bottom', portalClassName: null };

class MapTooltip extends Component<Props, State> {
  popper: any;

  targetRef: any;

  timeout: Nullable<ReturnType<typeof setTimeout>>;

  static defaultProps = defaultProps;

  constructor(props: Props) {
    super(props);

    this.popper = null;
    this.targetRef = null;

    this.state = {
      isOpen: false,
      isChildHover: false,
    };
    this.timeout = null;
  }

  componentDidMount() {
    if (this.targetRef) {
      const ref = this.targetRef;

      ref.addEventListener('mouseenter', this.onOpenTooltip);
      ref.addEventListener('mouseleave', this.onCloseTooltip);
    }
  }

  componentWillUnmount() {
    if (this.targetRef) {
      const ref = this.targetRef;

      ref.removeEventListener('mouseenter', this.onOpenTooltip);
      ref.removeEventListener('mouseleave', this.onCloseTooltip);
    }
  }

  onOpenTooltip = () => {
    this.timeout = setTimeout(() => {
      this.setState({ isOpen: true });
    }, DELAY_TIME);
  };

  onCloseTooltip = () => {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.setState({
      isOpen: false,
    });
  };

  onChildMouseEnter = () => {
    this.setState({
      isChildHover: true,
    });
  };

  onChildMouseLeave = () => {
    this.setState({
      isChildHover: false,
    });
  };

  onOpen = (tooltipElement: ReactElement, targetElement: ReactElement) => {
    // Note that GPU acceleration should be disabled otherwise Popper will use `translate3d`
    // which can cause blurriness in Safari and Chrome.
    const stdModifiers = [
      {
        name: 'computeStyles',
        options: {
          gpuAcceleration: false,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ];

    // @ts-expect-error TS2345: Argument of type 'ReactElement<any, string | JSXElementConstructor<any>>' is not assignable to parameter of type 'Element | VirtualElement'.
    this.popper = createPopper(targetElement, tooltipElement, {
      placement: this.props.placement,
      modifiers: stdModifiers,
    });

    this.popper.update();
  };

  beforeClose = (done: Function) => {
    if (this.popper) {
      this.popper.destroy();
      this.popper = null;
    }

    done();
  };

  render() {
    const { children, portalClassName, renderTarget, target } = this.props;

    return (
      <>
        {target}
        <Portal
          beforeClose={this.beforeClose}
          target={target}
          targetRef={(targetRef: any) => {
            this.targetRef = targetRef;
          }}
          isOpen={this.state.isOpen || this.state.isChildHover}
          onOpen={this.onOpen}
          onClose={this.onCloseTooltip}
          className={portalClassName}
          renderTarget={renderTarget}
        >
          <div
            onMouseEnter={this.onChildMouseEnter}
            onMouseLeave={this.onChildMouseLeave}
          >
            {children}
          </div>
        </Portal>
      </>
    );
  }
}

export default MapTooltip;
